import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import useSeasonSelection from "../../../hooks/useSeasonSelection";
import { seasonLabel } from "../../../services/seasons/seasonService";
import SeasonPicker from "../SeasonPicker/SeasonPicker";
import styles from "./SeasonNavControl.module.css";

const labelFor = (season) => {
  if (season.selectedYear == null) return "Season";

  return `${seasonLabel(season.selectedYear)}${
    season.selectedYear === season.currentYear ? " (on now)" : ""
  }`;
};

const pickerProps = (season) => ({
  years: season.years,
  currentYear: season.currentYear,
  selectedYear: season.selectedYear,
  spanLabel: season.spanLabel,
  isArchive: season.isArchive,
  phases: season.phases,
  activeWeek: season.activeWeek,
  onSelectWeek: season.selectWeek,
});

const SeasonNavControl = ({ variant = "desktop" }) => {
  const season = useSeasonSelection();
  const [open, setOpen] = useState(false);
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("keydown", closeOnEscape);
    closeRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  if (season.years.length === 0 || season.selectedYear == null) return null;

  const selectedLabel = labelFor(season);

  if (variant === "desktop") {
    return (
      <div className={styles.desktopControl}>
        <SeasonPicker
          {...pickerProps(season)}
          idPrefix="nav-season-desktop"
          compact
          showWeeks={false}
          showDetails={false}
          onSelectSeason={season.selectSeason}
        />
      </div>
    );
  }

  const selectSeasonAndClose = (year) => {
    season.selectSeason(year);
    setOpen(false);
  };

  const mobileSheet = open ? (
    <div className={styles.mobileLayer}>
      <button
        type="button"
        className={styles.backdrop}
        onClick={() => setOpen(false)}
        aria-label="Close season selector"
      />
      <section
        id="season-mobile-sheet"
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby="season-mobile-heading"
      >
        <div className={styles.sheetHeader}>
          <h2 id="season-mobile-heading">Season</h2>
          <button
            ref={closeRef}
            type="button"
            className={styles.closeButton}
            onClick={() => setOpen(false)}
            aria-label="Close season selector"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>

        <SeasonPicker
          {...pickerProps(season)}
          idPrefix="nav-season-mobile"
          showWeeks={false}
          showDetails={false}
          onSelectSeason={selectSeasonAndClose}
        />
      </section>
    </div>
  ) : null;

  return (
    <div className={styles.mobileControl}>
      <button
        type="button"
        className={styles.mobileButton}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="season-mobile-sheet"
        aria-label={`Season, ${selectedLabel}. Change season`}
        title={`Change season: ${selectedLabel}`}
      >
        <i className="fa-regular fa-calendar" aria-hidden="true" />
        <span>{season.selectedYear}</span>
      </button>

      {mobileSheet && createPortal(mobileSheet, document.body)}
    </div>
  );
};

export default SeasonNavControl;
